import CIMSCollapse from 'components/CIMSCollapse'
import { PermissionEditorProps } from './types'
import CIMSSection from 'components/CIMSSection'
import { useCallback, useEffect, useState } from 'react'
import {
  alphabeticalSort,
  BasicPermissionItem,
  CIMSModuleItem,
  CIMSPageItem,
  Permission,
  resolveValueOrArray,
} from '@cims-online/shared'
import PermissionPanelHeader from './components/PermissionPanelHeader'
import { Space, Typography } from 'antd'
import CIMSCheckbox from 'components/CIMSCheckbox'
import { CIMSTag } from 'components/CIMSTag'
import PermissionEditorButtons from './components/PermissionEditorButtons'
import { CIMSCollapseCountsExtraProps } from 'components/CIMSCollapse/components/CIMSCollapseCountsExtra/types'

const PermissionEditor: React.VFC<PermissionEditorProps> = ({
  roles,
  value,
  loading,
  disabled = false,
  onChange,
  help,
}) => {
  const [openPanels, _setOpenPanels] = useState<string[]>([])
  const [rolePermissions, setRolePermissions] = useState<string[]>([
    ...new Set(roles?.flatMap(({ permissions }) => permissions)),
  ])
  const [checked, _setChecked] = useState<string[]>([
    ...new Set([...(value ?? []), ...rolePermissions]),
  ])
  const setOpenPanels = (key: string | string[]) =>
    _setOpenPanels(resolveValueOrArray(key))
  const allPermissionsMap = Permission.moduleToPageToBasics
  const isChecked = useCallback(
    (permission: BasicPermissionItem) => checked.includes(permission.id),
    [checked]
  )
  const setChecked = useCallback(
    (state: boolean, permissionItems: BasicPermissionItem[]) => {
      if (state) {
        _setChecked((prev) => [
          ...prev,
          ...permissionItems
            .filter((permissionItem) => !isChecked(permissionItem))
            .map(({ id }) => id),
        ])
      } else {
        _setChecked((prev) =>
          prev.filter(
            (id) =>
              rolePermissions.includes(id) ||
              !permissionItems.some((item) => item.id === id)
          )
        )
      }
    },
    [isChecked, rolePermissions]
  )
  const permissionCounts = useCallback(
    (
      item: CIMSModuleItem | CIMSPageItem
    ): Pick<CIMSCollapseCountsExtraProps, 'type' | 'counts'> => {
      const permissions = Permission.findByModuleOrPage(item)
      const roleCount = permissions.filter((permission) =>
        rolePermissions.includes(permission.id)
      ).length
      const addCount = permissions.filter(
        (permission) =>
          checked.includes(permission.id) &&
          !rolePermissions.includes(permission.id)
      ).length
      return {
        type: 'counts',
        counts: [
          {
            key: item.id + '-roles',
            count: roleCount,
            showZero: false,
            backgroundColor: 'grey',
            children: 'Role Permissions',
          },
          {
            key: item.id + '-additional',
            count: addCount,
            showZero: false,
            children: roles ? 'Additional' : undefined,
          },
        ],
      }
    },
    [roles, rolePermissions, checked]
  )
  useEffect(() => {
    onChange?.(checked)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked])
  useEffect(() => {
    _setChecked((prevChecked) => {
      const newChecked = prevChecked.filter(
        (permissionId) => !rolePermissions.includes(permissionId)
      )
      setRolePermissions([
        ...new Set(roles?.flatMap(({ permissions }) => permissions)),
      ])
      return newChecked
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles])
  useEffect(() => {
    _setChecked((prevChecked) => [
      ...new Set([...prevChecked, ...rolePermissions]),
    ])
  }, [rolePermissions])
  return (
    <CIMSSection help={help} title="Permissions">
      <PermissionEditorButtons
        {...{
          disabled,
          roles,
          rolePermissions,
          checked,
          setChecked,
          _setChecked,
          openPanels,
          setOpenPanels,
        }}
      />
      <CIMSCollapse
        multiple
        collapsible={disabled ? 'disabled' : undefined}
        loading={loading}
        activeKey={openPanels}
        onChange={setOpenPanels}
        panels={[...allPermissionsMap.keys()]
          .sort(alphabeticalSort('name'))
          .map((moduleItem) => {
            return {
              key: moduleItem.id,
              extra: permissionCounts(moduleItem),
              header: {
                children: (
                  <PermissionPanelHeader
                    item={moduleItem}
                    {...{
                      loading,
                      disabled,
                      rolePermissions,
                      isChecked,
                      setChecked,
                    }}
                  />
                ),
              },
              children: (
                <CIMSCollapse
                  multiple
                  activeKey={openPanels}
                  onChange={setOpenPanels}
                  panels={[...allPermissionsMap.get(moduleItem)!.keys()]
                    .sort(alphabeticalSort('name'))
                    .map((pageItem) => ({
                      key: pageItem.id,
                      extra: permissionCounts(pageItem),
                      header: {
                        children: (
                          <PermissionPanelHeader
                            item={pageItem}
                            {...{
                              loading,
                              disabled,
                              rolePermissions,
                              isChecked,
                              setChecked,
                            }}
                          />
                        ),
                      },
                      children: (
                        <Space
                          size={'middle'}
                          direction="vertical"
                          style={{ margin: '10px 15px' }}
                        >
                          {[
                            ...allPermissionsMap
                              .get(moduleItem)!
                              .get(pageItem)!,
                          ]
                            .sort(alphabeticalSort('name'))
                            .map((permissionItem, i) => {
                              const roleNames = roles
                                ?.filter(({ permissions }) =>
                                  permissions.includes(permissionItem.id)
                                )
                                .map(({ name }) => ` ${name}`)
                              return (
                                <Space size="small" align="center">
                                  <CIMSCheckbox
                                    key={`${permissionItem.id}-${i}`}
                                    checked={checked.includes(
                                      permissionItem.id
                                    )}
                                    tooltipPlacement={'left'}
                                    disabled={[
                                      {
                                        disabled: loading ?? false,
                                        reason: 'loading',
                                      },
                                      {
                                        disabled: rolePermissions.includes(
                                          permissionItem.id
                                        ),
                                        reason: {
                                          why: 'disabled',
                                          message: `Assigned by Role${
                                            (roleNames?.length ?? 0) > 1
                                              ? 's'
                                              : ''
                                          }: ${roleNames}`,
                                        },
                                      },
                                    ]}
                                    onChange={() =>
                                      setChecked(!isChecked(permissionItem), [
                                        permissionItem,
                                      ])
                                    }
                                  />
                                  <Typography.Text>
                                    <CIMSTag>{permissionItem.name}</CIMSTag>
                                    {permissionItem.description}
                                  </Typography.Text>
                                </Space>
                              )
                            })}
                        </Space>
                      ),
                    }))}
                />
              ),
            }
          })}
      />
    </CIMSSection>
  )
}

export default PermissionEditor
